import {Modal} from 'antd';
import moment from 'moment';
import React from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';
import AvailabilityCalendar from './AvailabilityCalendar';
import AvailabilityEnd from './AvailabilityEnd';
import AvailabilityStart from './AvailabilityStart';
import LoadingIndicator from '../../common/loading/LoadingIndicator';
import {fetchAvailabilities, fetchAvailabilitiesDms} from '../../../actions/availabilitiesActions';
import {isLoadingAvailabilities, isLoadingAvailabilitiesDms, selectFetchAvailabilitiesAvailabilities, selectFetchAvailabilitiesDms} from '../../../selectors/availabilitiesSelector';
import {isLoadingOpeningHours, selectFetchOpeningHours} from '../../../selectors/openingHoursSelector';
import {withAuthorizationV2} from '../../../services/Authorization';
import Constants from '../../../services/Constants';
import Intervention from '../../../services/Intervention';
import Time from '../../../services/Time';
import './AvailabilityPlanning.css';
import {useIntl} from "react-intl";

const END_SLOTS_INTERVAL = 5;  // minutes
const END_DATE_INTERVAL = 3;    // months
const AVAILABLE_CALENDAR_DAY_COUNT = moment.duration(END_DATE_INTERVAL, 'months').asDays();

const AvailabilityPlanning = ({availabilities, confirmationInProgress, dispatchConfirmAppointment, dispatchFetchAvailabilities, dispatchFetchAvailabilitiesDms, dispatchFetchOpeningHours, fetchedAvailabilitiesDms, intervention, loading, mainServices, planningConfiguration, onClose, openingHours, serviceDuration, validationErrors, visible}) => {
  const intl = useIntl();

  const [startDate, setStartDate] = React.useState(Time.dateTimeToDateForApi(intervention.startDate));
  const [startTime, setStartTime] = React.useState(Time.dateTimeToTimeForApi(intervention.startDate));
  const [endDate, setEndDate] = React.useState(Time.dateTimeToDateForApi(intervention.endDate));
  const [endTime, setEndTime] = React.useState(Time.dateTimeToTimeForApi(intervention.endDate));
  const [duringWorkingHours, setDuringWorkingHours] = React.useState(Intervention.duringWorkingHours(intervention));
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth());

  const setters = {
    startTime: setStartTime,
    endTime: setEndTime
  };

  React.useEffect(() => {
      const {journey: {properties = {}} = {}, id} = intervention;
      const {mobility} = properties.mobility;
      const {services, valueServicesCart} = properties.services;
      const oas = Intervention.isOAS(intervention);
      const servicesString = services.map(s => oas ? s.id : s.name);

      dispatchFetchOpeningHours();
      dispatchFetchAvailabilities(id, {
        services: servicesString,
        mobility: mobility.name,
        bookingToken: valueServicesCart && valueServicesCart.bookingToken
      });
      if (isDmsLinkEnabled()) {
        dispatchFetchAvailabilitiesDms(id, {services: servicesString});
      }
    }, []
  );

  const computeAvailableAvailabilities = () => {
    const twentyFourSevenAllowed = isTwentyFourSevenAllowed();
    return availabilities.filter(availability => {
      return availability.availableSlots.length || isTwentyFourSevenAllowedForCurrentDay(twentyFourSevenAllowed, availability);
    });
  }

  // compute the end dates and slots
  const computeAvailableEndDates = () => {
    if (!availabilities.length || !openingHours.dealerOpeningHours) {
      return [];
    }

    const interventionEndDate = Time.dateTimeToDateForApi(intervention.endDate);
    const interventionEndTime = Time.dateTimeToTimeForApi(intervention.endDate);
    const lowerDateBoundary = availabilities.map(availability => availability.day).sort()[0];
    const endDates = [];

    for (let index = 0; index <= AVAILABLE_CALENDAR_DAY_COUNT; index++) {
      const currentDay = moment(lowerDateBoundary).add(index, 'days').format(Constants.DATE_FORMAT_API);
      const openingHour = openingHours.dealerOpeningHours[Time.dayOfWeekAsStringUppercase(currentDay)];

      if (openingHour && openingHour.slots && openingHour.slots.length > 0) {
        const opening = moment(openingHour.slots[0].openTime, Constants.TIME_FORMAT_API);
        const closing = moment(openingHour.slots[openingHour.slots.length - 1].closeTime, Constants.TIME_FORMAT_API);
        let slots = [];

        for (let time = opening; time.isSameOrBefore(closing); time.add(END_SLOTS_INTERVAL, 'minutes')) {
          slots.push(time.format(Constants.TIME_FORMAT_API));
        }

        // If the end time does not exist anymore (planing has been updated in the meantime), we need to add it so we can still display it
        if (currentDay === interventionEndDate && !slots.includes(interventionEndTime)) {
          slots.push(interventionEndTime);
        }

        // We should not propose an end date/time === to the start date/time
        if (currentDay === startDate) {
          slots = slots.filter(time => time !== startTime);
        }

        endDates.push({
          day: currentDay,
          slots: slots.sort((a, b) => Time.timeComparator(a, b))
        })

      } else if (currentDay === interventionEndDate) {
        endDates.push({
          day: currentDay,
          slots: [interventionEndTime],
        })
      }
    }

    return endDates;
  }

  const computeAvailableStartDates = () => {
    const twentyFourSevenAllowed = isTwentyFourSevenAllowed();
    const interventionStartDate = Time.dateTimeToDateForApi(intervention.startDate);
    const interventionStartTime = Time.dateTimeToTimeForApi(intervention.startDate);

    return availabilities
      .filter(availability => [Constants.DAY_OPEN.value, Constants.DAY_OVERBOOKED.value].includes(availability.status) || availability.day === interventionStartDate)
      .map(availability => {
        const {day, availableSlots = []} = availability;
        const slots = availableSlots.map(slot => slot.startTime);
        if (day === interventionStartDate && !slots.includes(interventionStartTime) && Intervention.duringWorkingHours(intervention)) {
          // Add the actual start time if it was not present initially in the availabilities
          slots.push(interventionStartTime);
        }
        return {
          day,
          twentyFourSevenAvailable: isTwentyFourSevenAllowedForCurrentDay(twentyFourSevenAllowed, availability),
          slots: slots.sort((a, b) => Time.timeComparator(a, b))
        };
      })
      .filter(availability => availability.twentyFourSevenAvailable || availability.slots.length);
  }

  const isDmsLinkEnabled = () => {
    const {dmsLinkConfiguration = {}} = planningConfiguration;
    if (!dmsLinkConfiguration.active) {
      return false;
    }
    return intervention.journey.type === Constants.TYPE_APPOINTMENT.value;
  }

  const isTwentyFourSevenAllowed = () => {
    if (!Intervention.isTypeAppointmentOrBodywork(intervention) || Intervention.isWeb(intervention)) {
      return false;
    }
    const {journey: {properties: {mobility} = {}}} = intervention;
    return !Constants.MOBILITY_24_7.includes(mobility.name) || mainServices.offering.rentalPickup;
  };

  const isTwentyFourSevenAllowedForCurrentDay = (twentyFourSevenAllowed, availability) => {
    const initialDay = moment(intervention.startDate).format(Constants.DATE_FORMAT_API);
    return (twentyFourSevenAllowed && availability.twentyFourSevenAvailable) || (availability.day === initialDay && !Intervention.duringWorkingHours(intervention))
  }

  const onPropagateNavigate = (selectedMonth) => {
    setSelectedMonth(selectedMonth)
  }

  const onSave = () => {
    dispatchConfirmAppointment({
      id: intervention.id,
      dates: {
        startDate,
        startTime,
        endDate,
        endTime,
        duringWorkingHours
      }
    });
  };

  const onDateStartDateChange = (event) => {
    const selectedDay = computeAvailableStartDates().find(d => d.day === event.target.value);
    setDuringWorkingHours(selectedDay.twentyFourSevenAvailable ? duringWorkingHours : true);
    setStartTime(selectedDay.slots.find(slot => slot >= startTime) || selectedDay.slots[0]); // try to find the same element, or else take the first available
    setStartDate(event.target.value);
  };

  const onDateEndDateChange = (event) => {
    const selectedDay = computeAvailableEndDates().find(d => d.day === event.target.value);
    setEndTime(selectedDay.slots.find(slot => slot >= endTime) || selectedDay.slots[0]);     // Try to find the same element, or else take the first available
    setEndDate(event.target.value);
  };

  const onTimeChange = (event) => {
    setters[event.target.name](event.target.value);
  };

  const onTwentyFourChange = (event) => {
    setStartTime(computeAvailableStartDates().find(d => d.day === startDate).slots[0]);
    setDuringWorkingHours(!event.target.checked);
  };

  const selectedDates = {startDate, startTime, endDate, endTime, duringWorkingHours};
  const dmsLinkEnabled = isDmsLinkEnabled();
  const filteredAvailabilities = computeAvailableAvailabilities();
  const isConfirmed = Intervention.isConfirmed(intervention);
  const isRentalAssigned = intervention.journey.rentalVehicle;


  return (
    <Modal title={intl.formatMessage({id: 'appointment.detail.modal.planning'})} width={dmsLinkEnabled ? 1100 : 500} visible={visible} onOk={onSave} onCancel={confirmationInProgress ? undefined : onClose}
           confirmLoading={confirmationInProgress} okButtonProps={{disabled: confirmationInProgress}} cancelButtonProps={{disabled: confirmationInProgress}}>
      {
        (loading) ? (
          <LoadingIndicator/>
        ) : (
          <div>
            {isConfirmed && isRentalAssigned &&
              <div className="alert alert--error">{intl.formatMessage({id: 'warning.appointment.confirmed.dates'})}</div>
            }
            <div className={"row"}>
              {dmsLinkEnabled &&
                <React.Fragment>
                  <div className={"col-xs-12 col-sm-6"}>
                    <section className="box calendar-box">
                      <h3 className="box__subtitle">SAM</h3>
                      <AvailabilityCalendar selectedMonth={selectedMonth} onPropagateNavigate={onPropagateNavigate} intervention={intervention} availabilities={filteredAvailabilities} config={{mobility: true}}/>
                    </section>
                  </div>
                  <div className={"col-xs-12 col-sm-6"}>
                    <section className="box calendar-box">
                      <h3 className="box__subtitle">DMS</h3>
                      <AvailabilityCalendar selectedMonth={selectedMonth} onPropagateNavigate={onPropagateNavigate} intervention={intervention} availabilities={fetchedAvailabilitiesDms.availabilities} error={fetchedAvailabilitiesDms.error}/>
                    </section>
                  </div>
                </React.Fragment>
              }
              {!dmsLinkEnabled &&
                <div className={"col-xs-12 col-sm-12"}>
                  <AvailabilityCalendar selectedMonth={selectedMonth} onPropagateNavigate={onPropagateNavigate} intervention={intervention} availabilities={filteredAvailabilities} config={{mobility: true}}/>
                </div>
              }
            </div>

            <br/>
            <div className="table-status--error">
              {serviceDuration && `${intl.formatMessage({id: 'placeholder.duration'})} : ${Time.durationToMoment(serviceDuration).format(Constants.TIME_FORMAT_DISPLAY)}`}
            </div>
            <br/>

            <AvailabilityStart availabilities={computeAvailableStartDates()} selectedDates={selectedDates}
                               onDateChange={onDateStartDateChange} onTimeChange={onTimeChange} onTwentyFourChange={onTwentyFourChange}
                               error={validationErrors}/>

            <AvailabilityEnd availabilities={computeAvailableEndDates()} selectedDates={selectedDates} serviceDuration={serviceDuration}
                             onDateChange={onDateEndDateChange} onTimeChange={onTimeChange}
                             error={validationErrors}/>
            <div className="alert alert--warn">{intl.formatMessage({id: 'warning.appointment.changing.dates'})}</div>
          </div>
        )
      }
    </Modal>
  )

}

const mapStateToProps = (state) => {
  return {
    availabilities: selectFetchAvailabilitiesAvailabilities(state),
    fetchedAvailabilitiesDms: selectFetchAvailabilitiesDms(state),
    openingHours: selectFetchOpeningHours(state),
    confirmationInProgress: state.fetchIntervention.confirmationInProgress,
    serviceDuration: state.fetchAvailabilities.serviceDuration,
    validationErrors: state.fetchIntervention && state.fetchIntervention.error && state.fetchIntervention.error.content,
    loading: createSelector(
      [
        isLoadingAvailabilities,
        isLoadingAvailabilitiesDms,
        isLoadingOpeningHours
      ],
      (...args) => args.some(isLoading => isLoading)
    )(state)
  };
};

const mapDispatchToProps = dispatch => ({
  dispatchFetchAvailabilities: (id, payload) => dispatch(fetchAvailabilities({id, payload})),
  dispatchFetchAvailabilitiesDms: (id, payload) => dispatch(fetchAvailabilitiesDms({id, payload})),
  dispatchFetchOpeningHours: () => dispatch({type: 'FETCH_OPENING_HOURS.REQUEST'}),
  dispatchConfirmAppointment: (body) => dispatch({type: 'SAVE.INTERVENTION_CONFIRMATION.REQUEST', body})
});


export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withAuthorizationV2(AvailabilityPlanning, ['DUI_ADMIN', 'DUI_SERVICE_ADVISOR']));
